<script setup>
import DescriptionInline from './DescriptionInline.vue'
import ManageSubOrgBox from './ManageSubOrgBox.vue'
import ManageProgramBox from './ManageProgramBox.vue'
import ManageIterationBox from './ManageIterationBox.vue'
import ManageStreamBox from './ManageStreamBox.vue'
import ManageGroupBox from './ManageGroupBox.vue'
import api from '../api/api'
</script>

<template>
  <div class="mainDiv">
    <section class="body-container">
      <div class="org-header">
        <label for="selectOrganisation"
          ><DescriptionInline label="Organization"
        /></label>
        <input
          type="text"
          class="formControl"
          name="organization"
          :value="orgList.org_name"
          disabled
        />
      </div>

      <!-- Breadcrumb Navigation -->
      <div v-if="breadcrumbPath.length > 0" class="breadcrumb-container">
        <div class="breadcrumb">
          <span 
            v-for="(item, index) in breadcrumbPath" 
            :key="index"
            class="breadcrumb-item"
          >
            <span class="breadcrumb-text">{{ item }}</span>
            <span v-if="index < breadcrumbPath.length - 1" class="breadcrumb-separator">→</span>
          </span>
        </div>
      </div>

      <div class="tabs-container">
        <div class="tabs-header">
          <button 
            v-for="(tab, index) in tabs" 
            :key="index"
            :class="['tab-button', { active: activeTab === index }, { disabled: isTabDisabled(index) }]"
            @click="handleTabClick(index)"
            :disabled="isTabDisabled(index)"
          >
            {{ tab }}
          </button>
        </div>

        <div class="tab-content">
          <ManageSubOrgBox 
            v-show="activeTab === 0"
            :userData="userData" 
            :brandData="brandData" 
            @addedSuborg="newSuborgAdded"
            @updateSuborg="updateSuborg"
            @passedSuborgId="saveSuborgId"
            @suborgSelected="updateSuborgName"
          />
          
          <ManageProgramBox
            v-show="activeTab === 1"
            :userData="userData"
            :brandData="brandData"
            :newSuborg="newSuborg"
            :suborgId="suborgId"
            @addProgram="newProgramAdded"
            @updateProgram="updateProgram"
            @passedProgramId="saveProgramId"
            @programSelected="updateProgramName"
          /><ManageProgramBox
            v-show="activeTab === 1"
            :userData="userData"
            :brandData="brandData"
            :newSuborg="newSuborg"
            :suborgId="suborgId"
            @addProgram="newProgramAdded"
            @updateProgram="updateProgram"
            @passedProgramId="saveProgramId"
          />
          
          <ManageIterationBox
            v-show="activeTab === 2"
            :userData="userData"
            :brandData="brandData"
            :suborgId="suborgId"
            :programId="programId"
            :newProgram="newProgram"
            @addIteration="newIterationAdded"
            @updateIteration="updateIteration"
            @passedIterationId="saveIterationId"
            @iterationSelected="updateIterationName"
          />
          
          <ManageStreamBox
            v-show="activeTab === 3"
            :userData="userData"
            :brandData="brandData"
            :suborgId="suborgId"
            :programId="programId"
            :iterationId="iterationId"
            :newIteration="newIteration"
            @addStream="newStreamAdded"
            @updateStream="updateStream"
            @passedStreamId="saveStreamId"
            @streamSelected="updateStreamName"
          />
          
          <ManageGroupBox
            v-show="activeTab === 4"
            :userData="userData"
            :brandData="brandData"
            :suborgId="suborgId"
            :programId="programId"
            :iterationId="iterationId"
            :streamId="streamId"
            :newStream="newStream"
            @groupSelected="updateGroupName"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  component: [
    DescriptionInline,
    ManageSubOrgBox,
    ManageProgramBox,
    ManageIterationBox,
    ManageStreamBox,
    ManageGroupBox,
  ],
  props: {
    userData: Object,
    brandData: Object,
  },
  name: 'AdminDashboard',
  data: () => ({
    orgList: [],
    suborgId: '',
    programId: '',
    iterationId: '',
    streamId: '',
    newSuborg: null,
    newProgram: null,
    newIteration: null,
    newStream: null,
    activeTab: 0,
    tabs: ['Sub-Organizations', 'Programs', 'Iterations', 'Streams', 'Groups'],
    selectedNames: {
      suborg: '',
      program: '',
      iteration: '',
      stream: '',
      group: ''
    }
  }),
  computed: {
    breadcrumbPath() {
      const path = [];
      if (this.selectedNames.suborg) path.push(this.selectedNames.suborg);
      if (this.selectedNames.program) path.push(this.selectedNames.program);
      if (this.selectedNames.iteration) path.push(this.selectedNames.iteration);
      if (this.selectedNames.stream) path.push(this.selectedNames.stream);
      if (this.selectedNames.group) path.push(this.selectedNames.group);
      return path;
    }
  },
  mounted() {
    api
      .get(`organizations/${this.userData.org_id}`)
      .then((response) => {
        this.orgList = response.data
      })
      .catch((e) => {
        this.$flashMessage.show({
          type: 'error',
          title: e.message,
          message: '',
        })
      })
  },
  methods: {
    handleTabClick(index) {
      if (!this.isTabDisabled(index)) {
        this.activeTab = index
      }
    },
    isTabDisabled(index) {
      // Sub-Organizations tab is always enabled
      if (index === 0) return false
      
      // Programs tab requires suborg selection
      if (index === 1) return !this.suborgId
      
      // Iterations tab requires program selection
      if (index === 2) return !this.programId
      
      // Streams tab requires iteration selection
      if (index === 3) return !this.iterationId
      
      // Groups tab requires stream selection
      if (index === 4) return !this.streamId
      
      return false
    },
    saveSuborgId(suborgId) {
      this.suborgId = suborgId
      // Clear downstream selections when suborg changes
      this.programId = ''
      this.iterationId = ''
      this.streamId = ''
      
      // Clear downstream names
      this.selectedNames.program = ''
      this.selectedNames.iteration = ''
      this.selectedNames.stream = ''
      this.selectedNames.group = ''
      
      // Switch to Programs tab automatically if we're on Sub-Organizations tab
      if (this.activeTab === 0) {
        this.activeTab = 1
      }
    },
    updateSuborgName(name) {
      this.selectedNames.suborg = name
    },
    saveProgramId(programId) {
      this.programId = programId
      // Clear downstream selections when program changes
      if (!programId) {
        this.iterationId = ''
        this.streamId = ''
        
        // Clear downstream names
        this.selectedNames.iteration = ''
        this.selectedNames.stream = ''
        this.selectedNames.group = ''
      }
    },
    updateProgramName(name) {
      this.selectedNames.program = name
    },
    saveIterationId(iterationId) {
      this.iterationId = iterationId
      // Clear downstream selections when iteration changes
      if (!iterationId) {
        this.streamId = ''
        
        // Clear downstream names
        this.selectedNames.stream = ''
        this.selectedNames.group = ''
      }
    },
    updateIterationName(name) {
      this.selectedNames.iteration = name
    },
    saveStreamId(streamId) {
      this.streamId = streamId
      if (!streamId) {
        this.selectedNames.group = ''
      }
    },
    updateStreamName(name) {
      this.selectedNames.stream = name
    },
    updateGroupName(name) {
      this.selectedNames.group = name
    },
    newSuborgAdded(data) { 
      this.newSuborg = data
    },
    newProgramAdded(data) { 
      this.newProgram = data
    },
    newIterationAdded(data) { 
      this.newIteration = data
    },
    newStreamAdded(data) { 
      this.newStream = data
    },
    updateSuborg(data) { 
      this.newSuborg = data
    },
    updateProgram(data) { 
      this.newProgram = data
    },
    updateIteration(data) { 
      this.newIteration = data
    },
    updateStream(data) { 
      this.newStream = data
    },
  },
}
</script>

<style scoped>
.mainDiv {
  width: 100%;
}

.body-container {
  width: 100%;
  background: white;
  padding: 30px;
  box-shadow: 0px 2px 10px -4px #000000;
  /* border-radius: 20px; */
}

.org-header {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.breadcrumb-container {
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #F47820;
}

.breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breadcrumb-text {
  font-size: 14px;
  font-weight: 500;
  color: #212529;
}

.breadcrumb-separator {
  color: #6c757d;
  font-weight: 600;
  font-size: 14px;
}

.tabs-container {
  margin-top: 20px;
}

.tabs-header {
  display: flex;
  gap: 5px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 20px;
  overflow-x: auto;
}

.tab-button {
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.tab-button:hover {
  color: #333;
  background: #f5f5f5;
}

.tab-button.active {
  color: #212529;
  border-bottom-color: #F47820;
  font-weight: 600;
}

.tab-button.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.mt-3 {
  margin-top: 1.5rem;
}

.tab-content {
  padding: 20px 0;
  min-height: 400px;
}

.formControl {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border-width: 0 0 1px;
  border-color: grey;
  padding: 2px 5px;
  margin-bottom: 1em;
  outline: 0;
  width: 100%;
}

input, select, option, textarea {
  border: 1px solid grey;
}

@media only screen and (min-width: 280px) and (max-width: 900px) {
  .body-container {
    width: 95%;
    padding: 15px;
  }

  .tabs-header {
    gap: 2px;
  }

  .tab-button {
    padding: 10px 16px;
    font-size: 12px;
  }

  .tab-content {
    padding: 15px 0;
  }
}
</style>
